import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '@/components/SEO';

const PrivacyPolicy = () => {
  const { lang } = useLanguage();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        path="/privacy"
        title={lang === 'cs' ? 'Ochrana osobních údajů | Tom Art Mask' : 'Privacy Policy | Tom Art Mask'}
        description={lang === 'cs' ? 'Zásady zpracování osobních údajů a GDPR informace pro Tom Art Mask.' : 'Privacy policy and GDPR information for Tom Art Mask.'}
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          {lang === 'cs' ? 'Zpět na hlavní stránku' : 'Back to homepage'}
        </Link>

        {lang === 'cs' ? <CzechPrivacy /> : <EnglishPrivacy />}
      </div>
    </div>
  );
};

const CzechPrivacy = () => (
  <article className="prose prose-invert max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
    <h1>Ochrana osobních údajů</h1>
    <p className="text-sm text-muted-foreground">Poslední aktualizace: {new Date().toLocaleDateString('cs-CZ')}</p>

    <h2>1. Správce osobních údajů</h2>
    <p>
      Správcem osobních údajů je:<br />
      <strong>Tomáš Veselý</strong> – OSVČ (fyzická osoba podnikající)<br />
      IČO: <strong>24634921</strong><br />
      Sídlo: <strong>Krátká 895/16, Podklášteří, 674 01 Třebíč</strong><br />
      Email: <a href="mailto:info@tomartmask.cz"><strong>info@tomartmask.cz</strong></a><br />
      Telefon: <a href="tel:+420739232870"><strong>+420 739 232 870</strong></a><br />
      <span className="text-sm">Neplátce DPH. Zapsáno v živnostenském rejstříku.</span>
    </p>

    <h2>2. Jaké osobní údaje zpracováváme</h2>
    <p>Prostřednictvím kontaktního formuláře a konfigurátoru masek na tomto webu zpracováváme:</p>
    <ul>
      <li>Jméno a příjmení</li>
      <li>E-mailovou adresu</li>
      <li>Telefonní číslo (volitelné)</li>
      <li>Obsah zprávy / poptávky</li>
    </ul>
    <p>Prostřednictvím chatbotu můžeme zpracovávat:</p>
    <ul>
      <li>E-mailovou adresu (pokud ji poskytnete)</li>
      <li>Telefonní číslo (pokud jej poskytnete)</li>
      <li>Obsah konverzace</li>
    </ul>

    <h2>3. Účel zpracování</h2>
    <ul>
      <li><strong>Vyřízení poptávky</strong> – odpověď na vaši zprávu, příprava nabídky, komunikace ohledně zakázky</li>
      <li><strong>Oprávněný zájem</strong> – ochrana práv správce, zlepšování služeb</li>
    </ul>

    <h2>4. Právní základ zpracování</h2>
    <p>
      Zpracování probíhá na základě <strong>čl. 6 odst. 1 písm. b) GDPR</strong> (plnění smlouvy / přijetí opatření před uzavřením smlouvy) a <strong>čl. 6 odst. 1 písm. f) GDPR</strong> (oprávněný zájem správce).
    </p>

    <h2>5. Doba uchovávání</h2>
    <p>
      Osobní údaje z kontaktních formulářů uchováváme po dobu nezbytnou pro vyřízení poptávky, maximálně <strong>3 roky</strong> od posledního kontaktu, pokud není potřeba je uchovávat déle z důvodu plnění zákonných povinností.
    </p>

    <h2>6. Cookies a služby třetích stran</h2>
    <p>Web používá pouze nezbytné minimum cookies a externích služeb:</p>
    <ul>
      <li><strong>Technické cookies (nezbytné)</strong> – ukládají Váš souhlas s cookies a jazykovou volbu. Bez nich web nefunguje. Právní základ: oprávněný zájem (čl. 6/1/f GDPR).</li>
      <li><strong>YouTube embed (volitelné)</strong> – přehrávač videí společnosti Google Ireland Ltd. Načítá se až po Vašem souhlasu. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Zásady Google</a>.</li>
      <li><strong>Facebook Page Plugin (volitelné)</strong> – widget Meta Platforms Ireland Ltd. Načítá se až po Vašem souhlasu. <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Zásady Meta</a>.</li>
      <li><strong>Google Fonts</strong> – webové fonty (Bebas Neue, Inter) načítané ze serverů Google.</li>
    </ul>
    <p>Souhlas s volitelnými cookies můžete kdykoli odvolat smazáním údaje <code>cookie-consent</code> v úložišti prohlížeče.</p>

    <h2>7. Příjemci a zpracovatelé údajů</h2>
    <p>Vaše údaje zpracovávají následující zpracovatelé dle čl. 28 GDPR:</p>
    <ul>
      <li><strong>Lovable Cloud</strong> (provozovaná na infrastruktuře v EU) – hosting webu, databáze kontaktních zpráv a chatbotu.</li>
      <li><strong>Lovable AI Gateway</strong> – zpracování konverzace s chatbotem Arty (modely OpenAI / Google Gemini).</li>
      <li><strong>Google Ireland Ltd.</strong> – pouze při Vašem souhlasu s YouTube embedem.</li>
      <li><strong>Meta Platforms Ireland Ltd.</strong> – pouze při Vašem souhlasu s Facebook pluginem.</li>
    </ul>
    <p>Údaje nepředáváme třetím stranám pro marketingové účely.</p>

    <h2>8. Předávání údajů mimo EU</h2>
    <p>
      Pokud udělíte souhlas s embedy YouTube nebo Facebooku, mohou tyto společnosti zpracovávat údaje (zejména IP adresu) i mimo EU (USA). Předávání probíhá na základě <strong>standardních smluvních doložek (SCC)</strong> schválených Evropskou komisí.
    </p>

    <h2>9. Automatizované rozhodování</h2>
    <p>
      Chatbot Arty NEPROVÁDÍ automatizované rozhodování ve smyslu čl. 22 GDPR. Slouží pouze k informování zájemců a předávání kontaktů Tomáši Veselému, který sám posuzuje a vyřizuje každou poptávku osobně.
    </p>

    <h2>10. Vaše práva</h2>
    <p>Máte právo:</p>
    <ul>
      <li>Na <strong>přístup</strong> ke svým osobním údajům</li>
      <li>Na <strong>opravu</strong> nepřesných údajů</li>
      <li>Na <strong>výmaz</strong> údajů („právo být zapomenut")</li>
      <li>Na <strong>omezení zpracování</strong></li>
      <li>Na <strong>přenositelnost</strong> údajů</li>
      <li><strong>Vznést námitku</strong> proti zpracování</li>
      <li>Podat <strong>stížnost</strong> u Úřadu pro ochranu osobních údajů (<a href="https://www.uoou.cz" target="_blank" rel="noopener noreferrer">www.uoou.cz</a>)</li>
    </ul>

    <h2>11. Kontakt</h2>
    <p>
      V případě dotazů ohledně zpracování osobních údajů nás kontaktujte na: <a href="mailto:info@tomartmask.cz">info@tomartmask.cz</a>
    </p>
  </article>
);

const EnglishPrivacy = () => (
  <article className="prose prose-invert max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
    <h1>Privacy Policy</h1>
    <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString('en-US')}</p>

    <h2>1. Data Controller</h2>
    <p>
      The data controller is:<br />
      <strong>Tomáš Veselý</strong> – Sole Trader (OSVČ, self-employed)<br />
      Business ID (IČO): <strong>24634921</strong><br />
      Registered address: <strong>Krátká 895/16, Podklášteří, 674 01 Třebíč, Czech Republic</strong><br />
      Email: <a href="mailto:info@tomartmask.cz"><strong>info@tomartmask.cz</strong></a><br />
      Phone: <a href="tel:+420739232870"><strong>+420 739 232 870</strong></a><br />
      <span className="text-sm">Not a VAT payer. Registered in the Czech Trade Licensing Register.</span>
    </p>

    <h2>2. Personal Data We Process</h2>
    <p>Through the contact form and mask configurator on this website, we process:</p>
    <ul>
      <li>Full name</li>
      <li>Email address</li>
      <li>Phone number (optional)</li>
      <li>Message / inquiry content</li>
    </ul>
    <p>Through the chatbot, we may process:</p>
    <ul>
      <li>Email address (if provided)</li>
      <li>Phone number (if provided)</li>
      <li>Conversation content</li>
    </ul>

    <h2>3. Purpose of Processing</h2>
    <ul>
      <li><strong>Handling inquiries</strong> – responding to your message, preparing quotes, communication regarding orders</li>
      <li><strong>Legitimate interest</strong> – protecting the controller's rights, improving services</li>
    </ul>

    <h2>4. Legal Basis</h2>
    <p>
      Processing is based on <strong>Art. 6(1)(b) GDPR</strong> (performance of a contract / pre-contractual measures) and <strong>Art. 6(1)(f) GDPR</strong> (legitimate interest of the controller).
    </p>

    <h2>5. Data Retention</h2>
    <p>
      Personal data from contact forms is retained for the period necessary to handle the inquiry, up to a maximum of <strong>3 years</strong> from the last contact, unless longer retention is required by law.
    </p>

    <h2>6. Cookies and Third-Party Services</h2>
    <p>The website uses only the necessary minimum of cookies and external services:</p>
    <ul>
      <li><strong>Essential cookies</strong> – store your cookie consent and language preference. Required for the site to function. Legal basis: legitimate interest (Art. 6(1)(f) GDPR).</li>
      <li><strong>YouTube embeds (optional)</strong> – Google Ireland Ltd. video player. Loaded only after your consent. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.</li>
      <li><strong>Facebook Page Plugin (optional)</strong> – Meta Platforms Ireland Ltd. widget. Loaded only after your consent. <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a>.</li>
      <li><strong>Google Fonts</strong> – web fonts (Bebas Neue, Inter) loaded from Google servers.</li>
    </ul>
    <p>You can withdraw your consent at any time by clearing the <code>cookie-consent</code> entry in your browser storage.</p>

    <h2>7. Data Recipients and Processors</h2>
    <p>Your data is processed by the following processors under Art. 28 GDPR:</p>
    <ul>
      <li><strong>Lovable Cloud</strong> (EU infrastructure) – website hosting, contact message and chatbot database.</li>
      <li><strong>Lovable AI Gateway</strong> – chatbot conversation processing (OpenAI / Google Gemini models).</li>
      <li><strong>Google Ireland Ltd.</strong> – only if you consent to YouTube embeds.</li>
      <li><strong>Meta Platforms Ireland Ltd.</strong> – only if you consent to the Facebook plugin.</li>
    </ul>
    <p>Data is never shared with third parties for marketing purposes.</p>

    <h2>8. International Data Transfers</h2>
    <p>
      If you consent to YouTube or Facebook embeds, those providers may process data (notably your IP address) outside the EU (USA). Such transfers rely on <strong>Standard Contractual Clauses (SCC)</strong> approved by the European Commission.
    </p>

    <h2>9. Automated Decision-Making</h2>
    <p>
      The Arty chatbot DOES NOT perform automated decision-making within the meaning of Art. 22 GDPR. It only informs visitors and forwards leads to Tomáš Veselý, who personally reviews and handles every inquiry.
    </p>

    <h2>10. Your Rights</h2>
    <p>You have the right to:</p>
    <ul>
      <li><strong>Access</strong> your personal data</li>
      <li><strong>Rectify</strong> inaccurate data</li>
      <li><strong>Erase</strong> your data ("right to be forgotten")</li>
      <li><strong>Restrict</strong> processing</li>
      <li><strong>Data portability</strong></li>
      <li><strong>Object</strong> to processing</li>
      <li>Lodge a <strong>complaint</strong> with the Czech Data Protection Authority (<a href="https://www.uoou.cz" target="_blank" rel="noopener noreferrer">www.uoou.cz</a>)</li>
    </ul>

    <h2>11. Contact</h2>
    <p>
      For questions regarding personal data processing, contact us at: <a href="mailto:info@tomartmask.cz">info@tomartmask.cz</a>
    </p>
  </article>
);

export default PrivacyPolicy;
